import React, { useEffect, useState } from 'react';
import { CircularProgress, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroller';

import SnackBar from 'components/SnackBar';
import { SEARCH_DEBOUNCE_MS } from 'components/FiltersBar/constants';
import { PageToolbar, SearchField } from 'lib';
import {
  businessDirectoryActions,
  fetchContacts,
  selectContactFilters,
  selectContacts,
  selectContactsError,
  selectContactsHasMore,
  selectContactsLoading,
} from 'store/businessDirectory';
import { useDirectoryName } from 'common/hooks/use-directory-name';
import ContactCard from './components/ContactCard';
import FiltersDrawer, { defaultLocationId } from './components/FiltersDrawer';
import { styles } from './styles';

const useStyles = makeStyles(styles);

const BusinessDirectoryPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const directoryName = useDirectoryName();

  const contacts = useSelector(selectContacts);
  const hasMore = useSelector(selectContactsHasMore);
  const loading = useSelector(selectContactsLoading);
  const error = useSelector(selectContactsError);
  const filters = useSelector(selectContactFilters);
  const activeLocations = useSelector((state) => state.personalProfile.activeLocations);
  const orgMultiLocations = useSelector(
    (state) => !!state.personalProfile.organization?.settings?.multiLocations
  );
  const hasMultiLocationsPermission = useSelector(
    (state) => !!state.personalProfile.fullUserPermissions?.multiLocations
  );
  const canSeeLocations = orgMultiLocations && hasMultiLocationsPermission;

  const profileLoaded = useSelector((state) => !!state.personalProfile.user);
  const [search, setSearch] = useState(filters.searchValue);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => () => dispatch(businessDirectoryActions.reset()), []);

  useEffect(() => {
    // The default location comes from the profile, so seeding waits for it — and the first fetch waits for seeding.
    if (seeded || !profileLoaded) return;
    const locationId = canSeeLocations ? defaultLocationId(activeLocations) : '';
    if (locationId) dispatch(businessDirectoryActions.filtersChanged({ locationId }));
    setSeeded(true);
  }, [seeded, profileLoaded, canSeeLocations, activeLocations]);

  useEffect(() => {
    if (search === filters.searchValue) return undefined;
    const timer = setTimeout(
      () => dispatch(businessDirectoryActions.filtersChanged({ searchValue: search })),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (seeded) dispatch(fetchContacts());
  }, [filters, seeded]);

  return (
    <div className={classes.root}>
      <Typography variant="h3" className={classes.title}>
        {directoryName}
      </Typography>

      <PageToolbar
        className={classes.toolbar}
        search={<SearchField value={search} onChange={setSearch} />}
        onFiltersClick={() => setFiltersOpen(true)}
      />

      <FiltersDrawer
        open={filtersOpen}
        value={filters}
        showLocations={canSeeLocations}
        locations={activeLocations}
        onClose={() => setFiltersOpen(false)}
        onApply={(next) => {
          dispatch(businessDirectoryActions.filtersChanged(next));
          setFiltersOpen(false);
        }}
      />

      <InfiniteScroll
        // Remounts on every filter change so the scroller's page counter restarts with the rows.
        key={`${filters.searchValue}|${filters.locationId}|${filters.sortOrder}`}
        loadMore={() => dispatch(fetchContacts({ append: true }))}
        hasMore={hasMore && !loading}
        loader={
          <div className={classes.loader} key={0}>
            Loading ...
          </div>
        }
        threshold={400}
      >
        <Grid container spacing={2} justifyContent="flex-start">
          {contacts.map((contact) => (
            <Grid key={contact._id} item xs={12} sm={6} lg={3} xl="auto">
              <ContactCard
                fullName={contact.fullName}
                location={canSeeLocations ? contact.primaryLocation : ''}
                phoneNumber={contact.phoneNumber}
                email={contact.email}
              />
            </Grid>
          ))}
        </Grid>

        {!contacts.length &&
          (loading ? (
            <div className={classes.loader}>
              <CircularProgress size={32} />
            </div>
          ) : (
            <Typography variant="h3" align="center" className={classes.noResultsBlock}>
              No Results
            </Typography>
          ))}
      </InfiniteScroll>

      <SnackBar
        isOpen={!!error}
        type="error"
        text={error || ''}
        closeSnackbar={() => dispatch(businessDirectoryActions.errorCleared())}
      />
    </div>
  );
};

export default BusinessDirectoryPage;
